import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

import { getOverpaymentCapMonths, getSettingValueOrDefault } from '../settingutils';
import {
  AgencyRate,
  AgencyRateFeesType,
  AgencyRateOwner,
  Building,
  ReconciliationAllocationMode,
  ReconciliationSetting,
  ReconciliationSettingType,
  Setting,
  SettingType,
  Unit,
} from '../types/model-app';

import { getOwnerIdOfUnit } from './chargesUtils';

export const DEFAULT_RECONCILIATION_ALLOCATION_MODE = ReconciliationAllocationMode.AUTOMATIC;

export interface LeaseAllocationModeTarget {
  id: string;
  invoiceSenderId?: string | null;
}

export type LeaseAllocationModeResolver = (lease: LeaseAllocationModeTarget) => ReconciliationAllocationMode;

export const ALWAYS_AUTOMATIC_ALLOCATION_MODE_RESOLVER: LeaseAllocationModeResolver = () =>
  ReconciliationAllocationMode.AUTOMATIC;

export const isReconciliationAllocationMode = (value: string): value is ReconciliationAllocationMode =>
  Object.values(ReconciliationAllocationMode).includes(value as ReconciliationAllocationMode);

export const getClientAllocationMode = (settings?: Setting[]): ReconciliationAllocationMode => {
  const value = getSettingValueOrDefault(SettingType.RECONCILIATION_ALLOCATION_MODE, settings);
  return isReconciliationAllocationMode(value) ? value : DEFAULT_RECONCILIATION_ALLOCATION_MODE;
};

// Nothing makes an override unique per target; if two ever coexist, the stricter mode wins so a duplicate
// can never silently defeat the collected-rent safeguard
const pickOverride = (candidates: ReconciliationSetting[]): ReconciliationSetting | undefined =>
  candidates.find((override) => override.allocationMode === ReconciliationAllocationMode.DETAILED) ?? candidates[0];

export const getLeaseAllocationModeOverride = (
  leaseId: string,
  overrides: ReconciliationSetting[]
): ReconciliationSetting | undefined =>
  pickOverride(
    overrides.filter((override) => override.type === ReconciliationSettingType.LEASE && override.leaseId === leaseId)
  );

export const getOwnerAllocationModeOverride = (
  ownerId: string,
  overrides: ReconciliationSetting[]
): ReconciliationSetting | undefined =>
  pickOverride(
    overrides.filter((override) => override.type === ReconciliationSettingType.OWNER && override.ownerId === ownerId)
  );

export enum AllocationModeSource {
  RATE_CARD = 'RATE_CARD',
  LEASE = 'LEASE',
  OWNER = 'OWNER',
  CLIENT = 'CLIENT',
}

export interface ResolvedAllocationMode {
  allocationMode: ReconciliationAllocationMode;
  source: AllocationModeSource;
}

export const resolveOwnerAllocationModeWithSource = (
  ownerId: string | null | undefined,
  overrides: ReconciliationSetting[],
  clientAllocationMode: ReconciliationAllocationMode,
  collectedRentOwnerIds: string[] = []
): ResolvedAllocationMode => {
  if (isNil(ownerId)) {
    return { allocationMode: clientAllocationMode, source: AllocationModeSource.CLIENT };
  }
  // A rate card on collected rents imposes the detailed mode on its owners, above any exception
  if (collectedRentOwnerIds.includes(ownerId)) {
    return { allocationMode: ReconciliationAllocationMode.DETAILED, source: AllocationModeSource.RATE_CARD };
  }
  const ownerOverride = getOwnerAllocationModeOverride(ownerId, overrides);
  if (!isNil(ownerOverride)) {
    return { allocationMode: ownerOverride.allocationMode, source: AllocationModeSource.OWNER };
  }
  return { allocationMode: clientAllocationMode, source: AllocationModeSource.CLIENT };
};

export const resolveLeaseAllocationModeWithSource = (
  lease: LeaseAllocationModeTarget,
  overrides: ReconciliationSetting[],
  clientAllocationMode: ReconciliationAllocationMode,
  collectedRentOwnerIds: string[] = []
): ResolvedAllocationMode => {
  const ownerResolution = resolveOwnerAllocationModeWithSource(
    lease.invoiceSenderId,
    overrides,
    clientAllocationMode,
    collectedRentOwnerIds
  );
  if (ownerResolution.source === AllocationModeSource.RATE_CARD) {
    return ownerResolution;
  }
  const leaseOverride = getLeaseAllocationModeOverride(lease.id, overrides);
  if (!isNil(leaseOverride)) {
    return { allocationMode: leaseOverride.allocationMode, source: AllocationModeSource.LEASE };
  }
  return ownerResolution;
};

export const resolveLeaseAllocationMode = (
  lease: LeaseAllocationModeTarget,
  overrides: ReconciliationSetting[],
  clientAllocationMode: ReconciliationAllocationMode,
  collectedRentOwnerIds: string[] = []
): ReconciliationAllocationMode =>
  resolveLeaseAllocationModeWithSource(lease, overrides, clientAllocationMode, collectedRentOwnerIds).allocationMode;

export const buildLeaseAllocationModeResolver = (
  settings: Setting[] | undefined,
  overrides: ReconciliationSetting[],
  collectedRentOwnerIds: string[] = []
): LeaseAllocationModeResolver => {
  const clientAllocationMode = getClientAllocationMode(settings);
  return (lease) => resolveLeaseAllocationMode(lease, overrides, clientAllocationMode, collectedRentOwnerIds);
};

export interface LeaseAllocationPolicy {
  resolveAllocationMode: LeaseAllocationModeResolver;
  overpaymentCapMonths: number;
}

export const buildLeaseAllocationPolicy = (
  settings: Setting[] | undefined,
  overrides: ReconciliationSetting[],
  collectedRentOwnerIds: string[] = []
): LeaseAllocationPolicy => ({
  resolveAllocationMode: buildLeaseAllocationModeResolver(settings, overrides, collectedRentOwnerIds),
  overpaymentCapMonths: getOverpaymentCapMonths(settings),
});

export const isCollectedRentAgencyRate = (agencyRate: Pick<AgencyRate, 'feesAppliedTo'>): boolean =>
  agencyRate.feesAppliedTo === AgencyRateFeesType.AMOUNT_COLLECTED;

export const getOwnerIdsCoveredByAgencyRate = (
  agencyRate: Pick<AgencyRate, 'id' | 'unitIds'>,
  agencyRateOwners: AgencyRateOwner[],
  units: Unit[],
  getBuilding: (id: string) => Building | undefined
): string[] => {
  const ownerIdsFromRateOwners = agencyRateOwners.reduce<string[]>(
    (acc, agencyRateOwner) =>
      agencyRateOwner.agencyRateId === agencyRate.id ? [...acc, agencyRateOwner.contactId] : acc,
    []
  );
  const ownerIdsFromUnits = (agencyRate.unitIds ?? []).reduce<string[]>((acc, unitId) => {
    const unit = units.find((candidate) => candidate.id === unitId);
    if (isNil(unit)) {
      return acc;
    }
    const ownerId = getOwnerIdOfUnit(unit, getBuilding);
    return isNil(ownerId) ? acc : [...acc, ownerId];
  }, []);
  return uniq([...ownerIdsFromRateOwners, ...ownerIdsFromUnits]);
};

export const getOwnerIdsCoveredByCollectedRentAgencyRates = (
  agencyRates: AgencyRate[],
  agencyRateOwners: AgencyRateOwner[],
  units: Unit[],
  getBuilding: (id: string) => Building | undefined
): string[] =>
  uniq(
    agencyRates.reduce<string[]>(
      (acc, agencyRate) =>
        isCollectedRentAgencyRate(agencyRate)
          ? [...acc, ...getOwnerIdsCoveredByAgencyRate(agencyRate, agencyRateOwners, units, getBuilding)]
          : acc,
      []
    )
  );
